import { makeStyles, TextField } from '@material-ui/core'
import clsx from 'clsx'
import lunr from 'lunr'
import compact from 'lodash/compact'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'

import {
  Button,
  SearchInput,
  Select,
  Typography,
  mapToOptions,
} from 'src/modules/ui'

import ProductListItem from './ProductListItem'

const useStyles = makeStyles(theme => ({
  categoryName: {
    fontFamily: 'Gotham',
    fontWeight: 500,
    marginBottom: theme.spacing(-1),
    textTransform: 'uppercase',
  },
  productsHeader: {
    marginBottom: theme.spacing(1),
  },
  countLabel: {
    marginBottom: theme.spacing(2),
  },
  productTable: {
    width: '100%',
  },
  subcategoryName: {
    fontFamily: 'Gotham',
    fontWeight: 500,
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(2),
    textTransform: 'uppercase',
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('mobile')]: {
      flexDirection: 'row',
    },
  },
  toolbarCategory: {
    '& > *': {
      marginRight: theme.spacing(1),
    },
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      marginBottom: 0,
    },
  },
  toolbarGrow: {
    flexGrow: 1,
  },
  disputeNotes: {
    '& .MuiFilledInput-root': {
      background: 'rgb(255, 255, 255)',
    },
  },
}))

const getProductsByCategory = products =>
  products.reduce((byCat, product) => {
    const { category, subcategory } = product

    let cat = byCat.find(c => c.id === category.id)
    if (!cat) {
      cat = {
        ...category,
        subcategories: [],
      }
      byCat.push(cat)
    }

    let subcat = cat.subcategories.find(s => s.id === subcategory.id)
    if (!subcat) {
      subcat = {
        ...subcategory,
        products: [],
      }
      cat.subcategories.push(subcat)
    }

    subcat.products.push(product)

    return byCat
  }, [])

const ProductList = ({
  className,
  categoryColor,
  hideHeader,
  linkPrefix,
  navigateOnClick,
  noCategories,
  onMatchingProductsChange,
  productFilter,
  productPreCell,
  productPostCell,
  products,
  showDescription,
  showFilter,
  showPhoto,
  showPrices,
  showQuantity,
  showSubtotal,
  showSearch,
  showSupplierName,
  showDisputed,
  drilledDisputeNotes = {}, // TODO: convert this module to use redux
  stickHeaderClassName,
  subcategoryColor,
  tableClassName,
  toolbarPostFilterComponent,
  toolbarPreFilterComponent,
  useWindowScroll,
}) => {
  const classes = useStyles()
  const [category, setCategory] = useState()
  const [subcategory, setSubcategory] = useState()
  const [search, setSearch] = useState()
  const [searchResults, setSearchResults] = useState()

  /**
   * @param {*} e
   * @param {*} id
   */
  const handleDisputedNotesChange = (e, id) => {
    // FIXME: Total hack to get around the lack of
    // FIXME: a proper global state management system
    // FIXME: drilledDisputeNotes is an object that is passed in as a prop
    // FIXME: mutated here, and then passed back up to the parent component
    drilledDisputeNotes[id] = e.target.value
  }

  // Search
  useEffect(() => {
    if (search) {
      const index = lunr(function () {
        this.field('name')
        this.field('description')
        products.forEach(prod => this.add(prod))
      })
      const fuzzyTerms = search
        .trim()
        .split(' ')
        .map(t => `+${t}~1`)
        .join(' ')

      const results = index.search(fuzzyTerms)
      setSearchResults(results)
    } else {
      setSearchResults()
    }
  }, [search])

  // Transform products
  const productsMatchingSearch = useMemo(() => {
    const matchesSearch = p =>
      !searchResults || searchResults.find(r => Number(r.ref) === p.id)
    return products
      .filter(matchesSearch)
      .filter(productFilter)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [products, productFilter, searchResults])

  useEffect(() => {
    if (onMatchingProductsChange) {
      onMatchingProductsChange(productsMatchingSearch)
    }
  }, [productsMatchingSearch])

  // Transform products into category/subcategory
  const categoriesWithProducts = noCategories
    ? []
    : getProductsByCategory(productsMatchingSearch)

  // Matching products
  const matchesCategory = c => !category || c.id === category
  const matchesSubcat = sc => !subcategory || sc.id === subcategory
  const matchingProducts = noCategories
    ? productsMatchingSearch.length
    : categoriesWithProducts.reduce((matching, category) => {
        if (matchesCategory(category)) {
          matching = category.subcategories.reduce((matching, subcategory) => {
            if (matchesSubcat(subcategory)) {
              matching += subcategory.products.length
            }
            return matching
          }, matching)
        }
        return matching
      }, 0)

  const numCols =
    2 +
    compact([
      productPreCell,
      productPostCell,
      showSupplierName,
      showQuantity,
      showPrices,
    ]).length

  return (
    <div className={className}>
      {(showFilter || showSearch) && (
        <>
          <div className={classes.toolbar}>
            {toolbarPreFilterComponent && toolbarPreFilterComponent()}
            {showFilter && (
              <Filter
                categoriesWithProducts={categoriesWithProducts}
                category={category}
                subcategory={subcategory}
                setCategory={setCategory}
                setSubcategory={setSubcategory}
              />
            )}
            {toolbarPostFilterComponent && toolbarPostFilterComponent()}
            <div className={classes.toolbarGrow} />
            {showSearch && (
              <div>
                <SearchInput
                  buttonProps={{
                    color: 'lightGreen',
                  }}
                  className={classes.searchInput}
                  placeholder="Search product name..."
                  size="large"
                  value={search}
                  onChange={setSearch}
                />
              </div>
            )}
          </div>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.countLabel}
          >
            Showing {pluralize('product', matchingProducts, true)}
          </Typography>
        </>
      )}
      {noCategories ? (
        <ProductsNoCategory
          categoryColor={categoryColor}
          hideHeader={hideHeader}
          numCols={numCols}
          products={productsMatchingSearch}
          stickHeaderClassName={stickHeaderClassName}
          tableClassName={tableClassName}
          useWindowScroll={useWindowScroll}
        >
          {(product, idx) => (
            <ProductListItem
              {...product}
              key={`product-${idx}`}
              linkPrefix={linkPrefix}
              navigateOnClick={navigateOnClick}
              productPreCell={productPreCell}
              productPostCell={productPostCell}
              showDescription={showDescription}
              showPhoto={showPhoto}
              showQuantity={showQuantity}
              showSubtotal={showSubtotal}
              showPrices={showPrices}
              showSupplierName={showSupplierName}
              conditionalSupplementalComponent={
                showDisputed && (
                  <TextField
                    key={`product-${product.id}`}
                    style={{ marginBottom: '1rem' }}
                    onChange={e => {
                      handleDisputedNotesChange(e, product.id)
                    }}
                    fullWidth
                    label="Type problem"
                    multiline
                    variant="filled"
                    size="small"
                    className={classes.disputeNotes}
                  />
                )
              }
            />
          )}
        </ProductsNoCategory>
      ) : (
        <ProductsByCategory
          categoriesWithProducts={categoriesWithProducts}
          category={category}
          categoryColor={categoryColor}
          numCols={numCols}
          subcategory={subcategory}
          subcategoryColor={subcategoryColor}
          tableClassName={tableClassName}
          stickHeaderClassName={stickHeaderClassName}
          useWindowScroll={useWindowScroll}
        >
          {(product, idx) => (
            <ProductListItem
              {...product}
              key={`product-${idx}`}
              linkPrefix={linkPrefix}
              navigateOnClick={navigateOnClick}
              productPreCell={productPreCell}
              productPostCell={productPostCell}
              showDescription={showDescription}
              showPhoto={showPhoto}
              showQuantity={showQuantity}
              showPrices={showPrices}
              showSupplierName={showSupplierName}
            />
          )}
        </ProductsByCategory>
      )}
    </div>
  )
}

const Filter = ({
  categoriesWithProducts,
  category,
  subcategory,
  setCategory,
  setSubcategory,
}) => {
  const classes = useStyles()

  // Extract categories from products
  const categoryOptions = categoriesWithProducts.map(mapToOptions)
  const mainCategory = categoriesWithProducts.find(item => item.id === category)
  const subcategories = (mainCategory && mainCategory.subcategories) || []
  const subcategoryOptions = subcategories.map(mapToOptions)

  const handleMainCategoryChange = event => {
    setCategory(event.target.value)
    setSubcategory('')
  }

  const handleSubcategoryChange = event => {
    setSubcategory(event.target.value)
  }

  const handleClearAll = () => {
    setCategory('')
    setSubcategory('')
  }

  // Check whether there are any products in selected category
  const categoryPresent =
    category && categoriesWithProducts.some(c => c.id === category)

  const subcategoryPresent =
    categoryPresent &&
    subcategory &&
    categoriesWithProducts
      .find(c => c.id === category)
      .subcategories.some(sc => sc.id === subcategory)

  // Clear subcat/cat selectors if nothing within them
  useEffect(() => {
    if (subcategory && !subcategoryPresent) {
      setSubcategory()
    }
    if (category && !categoryPresent) {
      setCategory()
    }
  }, [categoryPresent, subcategoryPresent])

  return (
    <div className={classes.toolbarCategory}>
      <Select
        className={classes.filter}
        clearText="Any Category"
        color="lightGreen"
        onChange={handleMainCategoryChange}
        options={categoryOptions}
        placeholder="Category"
        value={categoryPresent && category}
      />
      {subcategories.length > 0 && (
        <>
          <Select
            className={classes.filter}
            clearText="Any Subcategory"
            color="lightGreen"
            onChange={handleSubcategoryChange}
            options={subcategoryOptions}
            placeholder="Subcategory"
            value={subcategoryPresent && subcategory}
          />
          <Button className={classes.clearButton} onClick={handleClearAll}>
            Clear
          </Button>
        </>
      )}
    </div>
  )
}

const ProductsByCategory = ({
  category,
  categoryColor,
  categoriesWithProducts,
  children,
  numCols,
  stickHeaderClassName,
  subcategory,
  subcategoryColor,
  tableClassName,
  useWindowScroll,
}) => {
  const classes = useStyles()

  const matchesCategory = c => !category || c.id === category
  const matchesSubcat = sc => !subcategory || sc.id === subcategory

  const productList = useMemo(() => {
    let lastCatId = -1
    return categoriesWithProducts.filter(matchesCategory).reduce(
      (list, cat) => {
        let groups = list.groups
        const products = [
          ...list.products,
          ...cat.subcategories.filter(matchesSubcat).reduce((list, subcat) => {
            let newCat = false
            if (cat.id !== lastCatId) {
              newCat = true
              lastCatId = cat.id
            }
            groups.push({
              ...subcat,
              newCat,
              category: cat,
            })
            return [...list, ...subcat.products]
          }, []),
        ]
        return {
          groups,
          products,
        }
      },
      {
        groups: [],
        products: [],
      }
    )
  }, [categoriesWithProducts])

  if (productList.products.length === 0) {
    return null
  }

  const groupContent = index => {
    const subcategory = productList.groups[index]
    return (
      <td colSpan={numCols}>
        {!!subcategory.newCat && (
          <Typography className={classes.categoryName} color={categoryColor}>
            {subcategory.category.name}
          </Typography>
        )}
        <Typography
          className={classes.subcategoryName}
          color={subcategoryColor}
          variant="body2"
        >
          {subcategory.name}
        </Typography>
      </td>
    )
  }

  const groupCounts = productList.groups.map(({ products }) => products.length)

  return (
    <div className={tableClassName}>
      <VirtualTable
        itemContent={idx => children(productList.products[idx], idx)}
        groupCounts={groupCounts}
        groupContent={groupContent}
        stickHeaderClassName={stickHeaderClassName}
        useWindowScroll={useWindowScroll}
      />
    </div>
  )
}

const ProductsNoCategory = ({
  categoryColor,
  children,
  hideHeader,
  numCols,
  products,
  stickHeaderClassName,
  tableClassName,
  useWindowScroll,
}) => {
  const classes = useStyles()

  return (
    <>
      {!hideHeader && (
        <Typography
          className={clsx(classes.categoryName, classes.productsHeader)}
          color={categoryColor}
        >
          Products
        </Typography>
      )}
      <div className={tableClassName}>
        <VirtualTable
          data={products}
          itemContent={(idx, product) => children(product, idx)}
          stickHeaderClassName={stickHeaderClassName}
          useWindowScroll={useWindowScroll}
        />
      </div>
    </>
  )
}

const VirtualTable = ({
  stickHeaderClassName,
  useWindowScroll = true,
  ...props
}) => {
  return (
    <Virtuoso
      overscan={50}
      components={{
        Item: ({ children, ...props }) => {
          return (
            <table {...props} style={{ width: '100%' }}>
              {children}
            </table>
          )
        },
        // Item: ({ children, ...props }) => {
        //   return React.cloneElement(
        //     { ...children },
        //     {
        //       rootProps: {
        //         ...props,
        //       },
        //     }
        //   )
        // },
        // Group: props => {
        //   return <tr {...props} />
        // },
        // List: React.forwardRef(({ style, children }, listRef) => {
        //   return (
        //     <table
        //       style={{ ...style, width: '100%' }}
        //       ref={listRef}
        //       children={children}
        //     />
        //   )
        // }),
        TopItemList: ({ style, children }) => {
          return (
            <div style={style} className={stickHeaderClassName}>
              {children}
            </div>
          )
        },
      }}
      useWindowScroll={useWindowScroll}
      {...props}
    />
  )
}

ProductList.propTypes = {
  className: PropTypes.string,
  linkPrefix: PropTypes.string,
  navigateOnClick: PropTypes.bool,
  noCategories: PropTypes.bool,
  productFilter: PropTypes.func,
  productPreCell: PropTypes.func,
  productPostCell: PropTypes.func,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      category: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      subcategory: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        category: PropTypes.number,
      }),
      code: PropTypes.string,
      name: PropTypes.string,
      available: PropTypes.bool,
      description: PropTypes.string,
      unit: PropTypes.string,
      price: PropTypes.string,
      photo: PropTypes.string,
      createdAt: PropTypes.string,
    })
  ),
  showDescription: PropTypes.bool,
  showFilter: PropTypes.bool,
  showPhoto: PropTypes.bool,
  showPrices: PropTypes.bool,
  showQuantity: PropTypes.bool,
  showSubtotal: PropTypes.bool,
  showSearch: PropTypes.bool,
  showSupplierName: PropTypes.bool,
  tableClassName: PropTypes.string,
  toolbarPostFilterComponent: PropTypes.func,
  toolbarPreFilterComponent: PropTypes.func,
}

ProductList.defaultProps = {
  navigateOnClick: true,
  productFilter: product => true,
  categoryColor: 'lightGreen',
  linkPrefix: '',
  showDescription: true,
  showPhoto: true,
  showPrices: true,
  showQuantity: false,
  showSupplerName: false,
  subcategoryColor: 'lightGreen',
}

export default ProductList
